import React, {
    useState,
    useCallback,
    useEffect,
    useContext,
    createContext,
} from "react";
import { useCustomerContextQuery } from "Shared/util/web-api-hooks";
import useUserContext from "Contexts/user-context";
import {
    CustomerContext,
    CustomerStatusInfo,
    CustomerAnnualKycFollowUp,
    ErpIntegrationResource,
} from "Shared/util/types";

type CustomerContextProps = {
    customerData?: CustomerContext;
    loadingCustomerData: boolean;
    updateCustomerStatus: (statusObject: CustomerStatusInfo) => void;
    updateCustomerUpdateAnnualKycFollowUp: (
        annualKycFollowUp: CustomerAnnualKycFollowUp,
    ) => void;
    updateActiveErpIntegration: (
        erpIntegration?: ErpIntegrationResource,
    ) => void;
};

const CustomerContextModel = createContext<CustomerContextProps>({
    loadingCustomerData: true,
} as CustomerContextProps);

const useCustomerContext = () => {
    const context = useContext(CustomerContextModel);
    if (context === undefined)
        throw new Error(
            "useCustomerContext must be used within a CustomerProvider",
        );
    return context;
};

export const CustomerProvider: React.FC<React.PropsWithChildren<{}>> = ({
    children,
}) => {
    const { activeCompanyId, loaded: userContextLoaded } = useUserContext();
    const [customerContext, loadingCustomerContext, _, reloadCustomerContext] =
        useCustomerContextQuery();
    const [customer, setCustomer] = useState<CustomerContext>(
        (customerContext || {}) as CustomerContext,
    );
    const [loading, setLoading] = useState(loadingCustomerContext);

    const handleUpdateStatus = useCallback(
        (statusObject: CustomerStatusInfo) => {
            setCustomer((prev) => ({
                ...prev,
                status: { ...prev.status, ...statusObject },
            }));
        },
        [],
    );

    const handleUpdateAnnualKycFollowUp = useCallback(
        (annualKycFollowUp: CustomerAnnualKycFollowUp) => {
            setCustomer((prev) => ({
                ...prev,
                annualKycFollowUp: {
                    ...prev.annualKycFollowUp,
                    ...annualKycFollowUp,
                },
            }));
        },
        [],
    );

    const handleUpdateActiveErpIntegration = useCallback(
        (erpIntegration?: ErpIntegrationResource) => {
            setCustomer((prev) => ({
                ...prev,
                activeErpIntegration: erpIntegration
                    ? { ...prev.activeErpIntegration, ...erpIntegration }
                    : undefined,
            }));
        },
        [],
    );

    useEffect(() => {
        if (loadingCustomerContext) {
            setLoading(true);
        }

        if (customerContext && !loadingCustomerContext) {
            setCustomer(customerContext);
            setLoading(false);
        }
    }, [customerContext, loadingCustomerContext]);

    useEffect(() => {
        if (activeCompanyId > 0 && userContextLoaded) {
            reloadCustomerContext();
        }
    }, [activeCompanyId, userContextLoaded]);

    const contextValues = {
        customerData: customer,
        loadingCustomerData: loading,
        updateCustomerStatus: handleUpdateStatus,
        updateCustomerUpdateAnnualKycFollowUp: handleUpdateAnnualKycFollowUp,
        updateActiveErpIntegration: handleUpdateActiveErpIntegration,
    };

    return (
        <CustomerContextModel.Provider value={contextValues}>
            {children}
        </CustomerContextModel.Provider>
    );
};

export default useCustomerContext;
